import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { usePlmProgramsQuery } from '../../../../types/generated-types';
import { usePlmProgramListSubSubscription } from '../../../../types/generated-types';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import { Notifier } from '../../../system/services/notificationManager';
import { useInjectableComponents } from '../../../system/services/injectableComponentsManager';
import { BottomMenuItem } from '../../../system/services/injectableComponentsManager';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';

const formatDate = (timestamp: number): string => {
  return new Date(timestamp).toLocaleDateString();
};

export const PlmProgramList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuthorizer();

  const { data, loading, error } = usePlmProgramsQuery({
    fetchPolicy: 'network-only',
  });
  const { data: subscriptionData } = usePlmProgramListSubSubscription();

  const plmPrograms = data?.plmPrograms || [];

  usePlmProgramListSubSubscription({
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (error) {
      Notifier.error('There was a problem fetching the PLM programs: ', error);
    }
  }, [error]);

  const { setContextMenuItems, setTitle, setTitlePath } =
    useInjectableComponents();

  const isSmall = useIsSmallScreen();

  useEffect(() => {
    const items: BottomMenuItem[] = [];

    if (can('create', 'PlmProgram')) {
      items.push({
        id: 'add-plmProgram-menu-item',
        label: isSmall ? 'Add PLM Program' : 'Add Peak Load Management Program',
        icon: <AddIcon fontSize="small" />,
        navTarget: '/plmPrograms/add-plmProgram',
      });
    }

    setContextMenuItems(items.length > 0 ? [{ items }] : undefined);

    return () => {
      setContextMenuItems(undefined);
    };
  }, [setContextMenuItems, can, isSmall]);

  React.useEffect(() => {
    setTitle(isSmall ? 'PLM Programs' : 'Peak Load Management Programs');
    setTitlePath('/plmPrograms');
    return () => {
      setTitle(undefined);
      setTitlePath(undefined);
    };
  }, [setTitle, setTitlePath, isSmall]);

  const handleView = (id: string) => {
    if (!can('view', 'PlmProgram')) {
      Notifier.error('You do not have permission to view PLM programs');
      return;
    }
    navigate(`/plmPrograms/${id}/view-plmProgram`);
  };

  const handleEdit = (id: string) => {
    if (!can('update', 'PlmProgram')) {
      Notifier.error('You do not have permission to edit PLM programs');
      return;
    }
    navigate(`/plmPrograms/${id}/edit-plmProgram`);
  };

  const handleDelete = (id: string) => {
    if (!can('delete', 'PlmProgram')) {
      Notifier.error('You do not have permission to delete PLM programs');
      return;
    }
    // Implement delete functionality
  };

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">
          Error loading PLM programs: {error.message}
        </Typography>
      </Box>
    );
  }

  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: 'programId',
      header: 'Program ID',
    },
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'partner',
      header: 'Partner',
      Cell: ({ row }) => {
        const partner = row.original.partner;
        return partner ? partner.name : row.original.partnerId || 'No partner';
      },
    },
    {
      accessorKey: 'season',
      header: 'Season',
      Cell: ({ cell }) => {
        const season = cell.getValue<string>();
        return season || '-';
      },
    },
    {
      accessorKey: 'startDate',
      header: 'Start Date',
      Cell: ({ cell }) => {
        const date = cell.getValue<number>();
        return date ? formatDate(date) : '-';
      },
    },
    {
      accessorKey: 'endDate',
      header: 'End Date',
      Cell: ({ cell }) => {
        const date = cell.getValue<number>();
        return date ? formatDate(date) : '-';
      },
    },
    {
      accessorKey: 'participationType',
      header: 'Participation Type',
      Cell: ({ cell }) => {
        const type = cell.getValue<string>();
        return type || '-';
      },
    },
    {
      accessorKey: 'incentiveDescription',
      header: 'Incentive',
      Cell: ({ cell }) => {
        const incentive = cell.getValue<string>();
        return incentive || '-';
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 150,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: '0px' }}>
          <Tooltip title="View">
            <IconButton onClick={() => handleView(row.original._id)}>
              <ViewIcon />
            </IconButton>
          </Tooltip>
          {can('update', 'PlmProgram') && (
            <Tooltip title="Edit">
              <IconButton onClick={() => handleEdit(row.original._id)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          {can('delete', 'PlmProgram') && (
            <Tooltip title="Delete">
              <IconButton onClick={() => handleDelete(row.original._id)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box width={'100%'}>
      <MaterialReactTable
        columns={columns}
        data={plmPrograms}
        defaultColumn={{ size: 100 }}
        state={{
          isLoading: loading,
          columnVisibility: {
            programId: false,
            startDate: !isSmall,
            endDate: false,
            participationType: !isSmall,
            season: !isSmall,
          },
        }}
        enableColumnFilters={false}
        enableColumnActions={false}
        enableColumnResizing={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableRowActions={false}
        enableRowSelection={false}
        enableTopToolbar={false}
        enablePagination={plmPrograms.length > 10}
        muiTableBodyRowProps={{ hover: false }}
        muiTableHeadCellProps={{
          sx: {
            fontWeight: 'bold',
            fontSize: '14px',
            p: '0.5rem',
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '14px',
            p: '0.5rem',
          },
        }}
        layoutMode="semantic"
      />
    </Box>
  );
};
